<template>
  <Breadcrumbs
      title="Edit Module"
      :breadcrumbs="[
      { label: 'Master Data', path: '' },
      { label: 'Module & Route Configurations', path: '/module-route-configurations' },
      { label: 'Edit Module', path: '' },
    ]"
  />

  <div class="container-fluid">
    <div class="row project-cards">
      <div class="col-sm-12">
        <div class="card">
          <div class="card-body">
            <!-- Loading Indicator -->
            <div v-if="loading" class="d-flex flex-column align-items-center justify-content-center py-5">
              <div class="spinner-border" style="color: #003366; width: 3rem; height: 3rem;" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-3 text-muted" style="font-size: 1rem;">Fetching module details...</p>
            </div>

            <!-- Form Content -->
            <div v-else>
              <div class="row mb-4">
                <div class="col-md-9 form-group">
                  <label>Module Name <span class="text-danger">*</span></label>
                  <input type="text" v-model="moduleName" class="form-control" placeholder="Enter module name" style="text-transform: uppercase;" :disabled="isSaving" />
                </div>
              <div class="col-md-3 form-group d-flex flex-column align-items-center justify-content-center">
                <label>Module Status</label>
                <div class="form-check form-switch mt-2">
                  <input class="form-check-input custom-toggle" type="checkbox" style="cursor: pointer;" v-model="isActive" :title="isActive ? 'Active' : 'Inactive'" :disabled="isSaving">
                </div>
              </div>
            </div>

            <!-- Submodules Section -->
            <div class="row mb-4">
              <div class="col-12">
                <h6 class="mb-0 font-weight-bold mb-3">Submodules</h6>
                <div class="d-flex mb-3">
                  <input type="text" v-model="newSubmoduleName" class="form-control me-2" placeholder="Enter submodule name" @keyup.enter="!isSaving && addSubmodule()" :disabled="isSaving" />
                  <button class="btn text-nowrap" style="background-color: #0B0909; border-color: #0B0909; border-radius: 50px; color: #e0e0e0;" type="button" @click="addSubmodule" :disabled="isSaving">
                    {{ isEditingSubmodule ? 'Edit Submodule' : 'Add Submodule' }}
                  </button>
                  <button v-if="isEditingSubmodule" class="btn btn-outline-secondary ms-2 text-nowrap" style="border-radius: 50px;" type="button" @click="cancelEditSubmodule" :disabled="isSaving">
                    Cancel
                  </button>
                </div>
                
                <div v-if="existingSubmodules.length > 0 || newSubmodules.length > 0" class="position-relative mb-4 pb-2">
                  <button v-if="showLeftArrow" @click="scrollLeft" class="btn btn-light shadow position-absolute" style="top: 50%; left: -15px; transform: translateY(-50%); z-index: 10; width: 35px; height: 35px; border-radius: 50%; padding: 0; display: flex; align-items: center; justify-content: center;">
                    <i class="fa fa-chevron-left text-primary" style="margin-left: -2px;"></i>
                  </button>

                  <div ref="scrollContainer" @scroll="handleScroll" class="d-flex flex-nowrap overflow-auto hide-scrollbar py-2 px-1" style="gap: 15px;">
                    <!-- Existing Submodules -->
                    <div 
                      v-for="submodule in existingSubmodules" 
                      :key="'ex-'+submodule.id" 
                      class="card mb-0 shadow-sm" 
                      style="min-width: 220px; flex: 0 0 auto; border: 1px solid #e0e0e0; border-radius: 8px;"
                    >
                      <div class="card-body d-flex flex-column justify-content-between p-3">
                        <div class="d-flex justify-content-between align-items-start mb-3">
                          <h6 class="font-weight-bold text-secondary text-truncate" :title="submodule.name">
                            <i class="fa fa-folder-open me-2"></i> {{ submodule.name }}
                          </h6>
                          <i class="fa fa-pencil text-primary" @click="!isSaving && editSubmodule('existing', submodule.id)" title="Edit Submodule" :style="{ cursor: isSaving ? 'not-allowed' : 'pointer', opacity: isSaving ? 0.5 : 1 }"></i>
                        </div>
                        <div class="d-flex justify-content-between align-items-center mt-auto">
                          <span class="text-muted small">
                            <strong>{{ getRouteCount(submodule.id) }}</strong> Route(s)
                          </span>
                          <div class="form-check form-switch mb-0">
                            <input class="form-check-input custom-toggle" type="checkbox" style="cursor: pointer;" v-model="submodule.is_active" :title="submodule.is_active ? 'Active' : 'Inactive'" :disabled="isSaving">
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- New Submodules -->
                    <div 
                      v-for="(subName, index) in newSubmodules" 
                      :key="'new-'+index" 
                      class="card mb-0 shadow-sm" 
                      style="min-width: 220px; flex: 0 0 auto; border: 1px solid #e0e0e0; border-radius: 8px;"
                    >
                      <div class="card-body d-flex flex-column justify-content-between p-3">
                        <div class="d-flex justify-content-between align-items-start mb-3">
                          <h6 class="font-weight-bold text-secondary text-truncate" :title="subName">
                            <i class="fa fa-folder-open me-2"></i> {{ subName }}
                          </h6>
                          <div>
                            <i class="fa fa-pencil text-primary me-2" @click="!isSaving && editSubmodule('new', subName)" title="Edit Submodule" :style="{ cursor: isSaving ? 'not-allowed' : 'pointer', opacity: isSaving ? 0.5 : 1 }"></i>
                            <i class="fa fa-trash text-danger" @click="!(isSaving || (isEditingSubmodule && editingSubmoduleType === 'new' && editingSubmoduleId === subName)) && removeNewSubmodule(subName)" title="Remove Submodule" :style="{ cursor: (isSaving || (isEditingSubmodule && editingSubmoduleType === 'new' && editingSubmoduleId === subName)) ? 'not-allowed' : 'pointer', opacity: (isSaving || (isEditingSubmodule && editingSubmoduleType === 'new' && editingSubmoduleId === subName)) ? 0.5 : 1 }"></i>
                          </div>
                        </div>
                        <div class="d-flex justify-content-between align-items-center mt-auto">
                          <span class="text-muted small">
                            <strong>{{ getRouteCount(subName) }}</strong> Route(s)
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <button v-if="showRightArrow" @click="scrollRight" class="btn btn-light shadow position-absolute" style="top: 50%; right: -15px; transform: translateY(-50%); z-index: 10; width: 35px; height: 35px; border-radius: 50%; padding: 0; display: flex; align-items: center; justify-content: center;">
                    <i class="fa fa-chevron-right text-primary" style="margin-right: -2px;"></i>
                  </button>
                </div>
              </div>
            </div>

            <div class="d-flex justify-content-between align-items-center mb-3 mt-4">
              <h6 class="mb-0 font-weight-bold">Routes</h6>
              <button class="btn btn-sm text-white" style="background-color: #003366; border-color: #003366;" @click="addRouteRow" :disabled="isSaving">
                <i class="fa fa-plus me-1"></i> Add Route
              </button>
            </div>

            <div class="table-responsive">
              <table class="table table-bordered table-hover">
                <thead class="table-light">
                  <tr>
                    <th>Route Name</th>
                    <th>Submodule</th>
                    <th>Page Type</th>
                    <th>URI</th>
                    <th>Module</th>
                    <th class="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(route, index) in routes" :key="index">
                    <td>
                      <input type="text" class="form-control" v-model="route.route_name" placeholder="Route Name" :disabled="isSaving" />
                    </td>
                    <td>
                      <select class="form-select" v-model="route.submodule" :disabled="isSaving">
                        <option :value="0">No Submodule</option>
                        <option v-for="sub in existingSubmodules" :key="'opt-ex-'+sub.id" :value="sub.id">{{ sub.name }}</option>
                        <option v-for="subName in newSubmodules" :key="'opt-new-'+subName" :value="subName">{{ subName }}</option>
                      </select>
                    </td>
                    <td>
                      <select class="form-select" v-model="route.page_type_id" :disabled="isSaving">
                        <option value="" disabled>Select Type</option>
                        <option v-for="type in availablePageTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
                      </select>
                    </td>
                    <td>
                      <input type="text" class="form-control" v-model="route.uri" placeholder="URI" style="text-transform: lowercase;" :disabled="isSaving" />
                    </td>
                    <td>
                      <select class="form-select" v-model="route.module_id" :disabled="isSaving || !route.isExisting">
                        <option v-for="mod in availableModules" :key="mod.id" :value="mod.id">{{ mod.name }}</option>
                      </select>
                    </td>
                    <td class="text-center align-middle">
                      <template v-if="route.isExisting">
                        <!-- Toggle for existing -->
                        <div class="form-check form-switch d-flex justify-content-center mb-0">
                          <input class="form-check-input custom-toggle" type="checkbox" style="cursor: pointer;" v-model="route.status" :title="route.status ? 'Disable Route' : 'Enable Route'" :disabled="isSaving">
                        </div>
                      </template>
                      <template v-else>
                        <!-- Delete for new -->
                        <i class="fa fa-trash text-danger" @click="!isSaving && removeRouteRow(index)" title="Remove Route" :style="{ fontSize: '18px', cursor: isSaving ? 'not-allowed' : 'pointer', opacity: isSaving ? 0.5 : 1 }"></i>
                      </template>
                    </td>
                  </tr>
                  <tr v-if="routes.length === 0">
                    <td colspan="5" class="text-center text-muted py-4">No routes found. Click 'Add Route' to begin.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="text-end mt-4">
              <button class="btn btn-danger me-2" @click="cancel" :disabled="isSaving">Cancel</button>
              <button class="btn btn-success" @click="save" :disabled="isSaving">
                <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {{ isSaving ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import { getPageTypesList, getModules, viewModuleDetails, updateModule, getSubModulesList } from '@/services/dataService';

export default {
  data() {
    return {
      token: localStorage.getItem('token_set'),
      loading: false,
      isSaving: false,
      moduleId: null,
      moduleName: '',
      isActive: true,
      newSubmoduleName: '',
      isEditingSubmodule: false,
      editingSubmoduleType: null,
      editingSubmoduleId: null,
      existingSubmodules: [],
      newSubmodules: [],
      availableModules: [],
      availablePageTypes: [],
      routes: [],
      showLeftArrow: false,
      showRightArrow: false
    }
  },
  watch: {
    existingSubmodules: {
      handler() {
        this.$nextTick(() => {
          this.checkScroll();
          setTimeout(() => this.checkScroll(), 150);
        });
      },
      deep: true
    },
    newSubmodules: {
      handler() {
        this.$nextTick(() => {
          this.checkScroll();
          setTimeout(() => this.checkScroll(), 150);
        });
      },
      deep: true
    }
  },
  async mounted() {
    this.moduleId = Number(this.$route.params.id) || null;
    if (this.moduleId) {
      this.loading = true;
      try {
        await Promise.all([
          this.fetchPageTypes(),
          this.fetchModules(),
          this.fetchSubModules(),
          this.fetchModuleDetails()
        ]);
      } finally {
        this.loading = false;
      }
    }
    window.addEventListener('resize', this.checkScroll);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkScroll);
  },
  methods: {
    async fetchPageTypes() {
      const toast = useToast();
      try {
        const response = await getPageTypesList(this.token);
        if (response.data.status) {
          this.availablePageTypes = response.data.data.map(type => ({
            id: Number(type.id),
            name: type.type_name
          }));
        } else {
          toast.error(response.data.message || "Failed to retrieve page types");
        }
      } catch (error) {
        toast.error("An error occurred while fetching page types.");
      }
    },
    async fetchModules() {
      const toast = useToast();
      try {
        const response = await getModules(this.token);
        if (response.data.status) {
          this.availableModules = response.data.data.map(mod => ({
            id: Number(mod.id),
            name: mod.name
          }));
        } else {
          toast.error(response.data.message || "Failed to retrieve modules");
        }
      } catch (error) {
        toast.error("An error occurred while fetching modules.");
      }
    },
    async fetchSubModules() {
      const toast = useToast();
      try {
        const response = await getSubModulesList(this.token, this.moduleId);
        if (response.data.status) {
          this.existingSubmodules = response.data.data.map(sub => ({
            id: Number(sub.id),
            name: sub.name || sub.submodule_name,
            is_active: sub.is_active == 1 || sub.is_active === true,
            original_is_active: sub.is_active == 1 || sub.is_active === true
          }));
        }
      } catch (error) {
        toast.error("An error occurred while fetching submodules.");
      }
    },
    async fetchModuleDetails() {
      const toast = useToast();
      try {
        const response = await viewModuleDetails(this.token, this.moduleId);
        if (response.data.status) {
          const data = response.data.data;
          this.moduleName = data.module_name;
          this.isActive = data.is_active !== undefined ? data.is_active : true;
          
          let flattenedRoutes = [];
          if (data.submodules) {
             data.submodules.forEach(sub => {
                const subId = Number(sub.submodule_id);
                if (sub.routes) {
                   sub.routes.forEach(route => {
                      flattenedRoutes.push({
                         ...route,
                         submodule_id: subId
                      });
                   });
                }
             });
          } else if (data.routes) {
             flattenedRoutes = data.routes;
          }

          this.routes = flattenedRoutes.map(route => ({
            module_route_mapping_id: route.module_route_mapping_id ? Number(route.module_route_mapping_id) : null,
            route_id: route.route_id ? Number(route.route_id) : null,
            id: route.route_id ? Number(route.route_id) : (route.id ? Number(route.id) : null),
            route_name: route.name || route.route_name,
            original_route_name: route.name || route.route_name,
            page_type_id: route.page_type ? Number(route.page_type.id) : (route.page_type_id ? Number(route.page_type_id) : ''),
            original_page_type_id: route.page_type ? Number(route.page_type.id) : (route.page_type_id ? Number(route.page_type_id) : ''),
            uri: route.uri,
            original_uri: route.uri,
            status: route.is_active,
            original_status: route.is_active,
            isExisting: true,
            module_id: Number(this.moduleId),
            original_module_id: Number(this.moduleId),
            submodule: route.submodule_id !== undefined ? Number(route.submodule_id) : 0,
            original_submodule: route.submodule_id !== undefined ? Number(route.submodule_id) : 0
          }));
        } else {
          toast.error(response.data.message || "Failed to retrieve module details");
        }
      } catch (error) {
        toast.error("An error occurred while fetching details.");
      }
    },
    editSubmodule(type, identifier) {
      this.isEditingSubmodule = true;
      this.editingSubmoduleType = type;
      this.editingSubmoduleId = identifier;
      
      if (type === 'existing') {
        const sub = this.existingSubmodules.find(s => s.id === identifier);
        if (sub) this.newSubmoduleName = sub.name;
      } else if (type === 'new') {
        this.newSubmoduleName = identifier;
      }
    },
    cancelEditSubmodule() {
      this.isEditingSubmodule = false;
      this.editingSubmoduleType = null;
      this.editingSubmoduleId = null;
      this.newSubmoduleName = '';
    },
    addSubmodule() {
      const toast = useToast();
      const name = this.newSubmoduleName.trim();
      if (!name) {
        toast.error("Submodule name cannot be empty.");
        return;
      }
      
      const existsInExisting = this.existingSubmodules.some(s => s.name.toLowerCase() === name.toLowerCase() && (this.editingSubmoduleType !== 'existing' || s.id !== this.editingSubmoduleId));
      const existsInNew = this.newSubmodules.some(n => n.toLowerCase() === name.toLowerCase() && (this.editingSubmoduleType !== 'new' || n !== this.editingSubmoduleId));
      
      if (existsInExisting || existsInNew) {
        toast.error("Submodule already exists.");
        return;
      }

      if (this.isEditingSubmodule) {
        if (this.editingSubmoduleType === 'existing') {
          const sub = this.existingSubmodules.find(s => s.id === this.editingSubmoduleId);
          if (sub) sub.name = name;
        } else if (this.editingSubmoduleType === 'new') {
          const oldName = this.editingSubmoduleId;
          const index = this.newSubmodules.indexOf(oldName);
          if (index !== -1) {
             this.newSubmodules.splice(index, 1, name);
          }
          // Update references in routes
          this.routes.forEach(r => {
             if (r.submodule === oldName) {
                r.submodule = name;
             }
          });
        }
        this.cancelEditSubmodule();
      } else {
        this.newSubmodules.push(name);
        this.newSubmoduleName = '';
      }
    },
    removeNewSubmodule(subName) {
      const toast = useToast();
      const isUsed = this.routes.some(r => r.submodule === subName);
      if (isUsed) {
        toast.error(`Cannot delete submodule "${subName}" because it is selected in one or more routes.`);
        return;
      }
      this.newSubmodules = this.newSubmodules.filter(n => n !== subName);
    },
    getRouteCount(subIdOrName) {
      return this.routes.filter(r => r.submodule === subIdOrName).length;
    },
    addRouteRow() {
      this.routes.push({
        module_route_mapping_id: null,
        route_id: null,
        id: null,
        route_name: '',
        page_type_id: '',
        uri: '',
        status: true,
        isExisting: false,
        module_id: this.moduleId,
        submodule: 0,
        original_submodule: 0
      });
    },
    removeRouteRow(index) {
      this.routes.splice(index, 1);
    },
    cancel() {
      this.$router.push('/module-route-configurations');
    },
    async save() {
      const toast = useToast();
      if (!this.moduleName) {
        toast.error("Module name is required.");
        return;
      }
      
      this.isSaving = true;

      const upperModuleName = this.moduleName.toUpperCase();

      const updated_routes = [];
      const new_routes = [];
      const changed_module = [];
      const changed_submodule = [];

      for (const route of this.routes) {
        if (!route.route_name || !route.page_type_id || !route.uri) {
           toast.error("Please fill all required fields for all routes.");
           this.isSaving = false;
           return;
        }
        
        const lowerUri = route.uri.toLowerCase();

        if (route.isExisting) {
           let moduleChanged = route.module_id !== route.original_module_id;
           let subChanged = route.submodule !== route.original_submodule;

           if (moduleChanged) {
              changed_module.push({
                 module_route_mapping_id: route.module_route_mapping_id,
                 module_id: route.module_id,
                 route_id: route.route_id,
                 submodule_id: typeof route.submodule === 'number' ? route.submodule : 0
              });
           }
           
           if (subChanged && !moduleChanged) {
              changed_submodule.push({
                 module_route_mapping_id: route.module_route_mapping_id,
                 module_id: route.module_id,
                 route_id: route.route_id,
                 submodule: route.submodule === "" ? 0 : route.submodule
              });
           } 
           
           if (!moduleChanged && !subChanged) {
              const routeChanged = route.route_name !== route.original_route_name ||
                                   route.page_type_id !== route.original_page_type_id ||
                                   route.uri !== route.original_uri ||
                                   route.status !== route.original_status;

              if (routeChanged) {
                 updated_routes.push({
                    module_route_mapping_id: route.module_route_mapping_id,
                    id: route.id,
                    page_type_id: route.page_type_id,
                    name: route.route_name,
                    uri: lowerUri,
                    is_active: route.status
                 });
              }
           }
        } else {
           new_routes.push({
              page_type_id: route.page_type_id,
              name: route.route_name,
              uri: lowerUri,
              submodule: route.submodule === "" ? 0 : route.submodule
           });
        }
      }

      const updated_submodules = this.existingSubmodules
        .filter(s => s.is_active !== s.original_is_active || s.name !== s.original_name)
        .map(s => ({
          id: s.id,
          name: s.name,
          is_active: s.is_active
        }));

      const payload = {
        module_name: upperModuleName,
        is_active: this.isActive,
        updated_routes: updated_routes,
        updated_submodules: updated_submodules,
        new_routes: new_routes,
        new_submodules: this.newSubmodules,
        changed_module: changed_module,
        changed_submodule: changed_submodule
      };

      try {
        const response = await updateModule(this.token, this.moduleId, payload);
        if (response.data.status) {
           toast.success(response.data.message || "Module updated successfully");
           this.$router.push('/module-route-configurations');
        } else {
           toast.error(response.data.message || "Failed to update module");
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to update module");
      } finally {
        this.isSaving = false;
      }
    },
    scrollLeft() {
      if (this.$refs.scrollContainer) {
        this.$refs.scrollContainer.scrollBy({ left: -250, behavior: 'smooth' });
      }
    },
    scrollRight() {
      if (this.$refs.scrollContainer) {
        this.$refs.scrollContainer.scrollBy({ left: 250, behavior: 'smooth' });
      }
    },
    handleScroll() {
      this.checkScroll();
    },
    checkScroll() {
      const container = this.$refs.scrollContainer;
      if (container) {
        this.showLeftArrow = container.scrollLeft > 0;
        this.showRightArrow = Math.ceil(container.scrollLeft + container.clientWidth) < container.scrollWidth;
      }
    }
  }
}
</script>

<style scoped>
.form-group label {
  font-weight: bold;
}
.custom-toggle {
  background-color: #dc3545 !important; /* Red when unchecked */
  border-color: #dc3545 !important;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e") !important;
}
.custom-toggle:checked {
  background-color: #198754 !important; /* Green when checked */
  border-color: #198754 !important;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e") !important;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>